import { Component, Input, Inject, OnInit, effect } from "@angular/core";
import { UntypedFormArray, UntypedFormGroup, Validators } from "@angular/forms";
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from "@angular/material-moment-adapter";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import { TranslateService } from "@ngx-translate/core";
import { HelperService, IdentifierService } from "merlin-core";
import { UserProfileObject, UserService } from "merlin-library";
import { Subscription } from "rxjs";
import { Calender, CustDateAdapter } from "../../../followup-schedule/Calender";
import { MyFormat } from "../date-format/my-date-format";
import {
    debounceTime,
    distinctUntilChanged,
    take,
} from 'rxjs/operators';
import { LocalizationService } from "src/core/utilitites/localization.service";
@Component({
    selector: 'app-manual-followup',
    styleUrls: ['./manual-followup.component.scss'],
    templateUrl: './manual-followup.component.html',
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
        {
            provide: DateAdapter,
            useClass: CustDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        { provide: MAT_DATE_FORMATS, useClass: MyFormat },
    ],
    standalone: false
})
export class ManualFollowupComponent implements OnInit {
    @Input() followupScheduleForm: UntypedFormGroup;
    manualScheduleForm: UntypedFormGroup;
    discDates: UntypedFormArray;
    @Input() lastTransMissionDate: string;
    @Input() locale: string;
    @Input() tz: string;
    calenderHeader = Calender;
    userProfile: UserProfileObject;
    datePlaceholder = '';
    localeSubscription: Subscription;

    public stringLabels = [
        "label.gb.last_followup",
        "label.gb.transmit_on",
        "label.gb.delta",
    ]

    public labels: Record<string, string>;

    constructor(
        public identifierService: IdentifierService,
        private translateService: TranslateService,
        private localizationService: LocalizationService,
        private helperService: HelperService,
        private userService: UserService,
        @Inject(MAT_DATE_FORMATS) private config: MyFormat,
        @Inject(DateAdapter) private dateAdapter: CustDateAdapter
    ) {
        effect(() => {
            const lang = this.localizationService.currentLang();
            if (!lang) return;
            this.labels = this.localizationService.reparseLocalizedStrings(this.labels);
        });
        this.userService
            .getUserObs()
            .pipe(take(1))
            .subscribe((userProfile: UserProfileObject) => {
                this.userProfile = userProfile;
            });

    }
    dateClass = (cellDate, view) => {
        if (![null, ''].includes(cellDate)) {
            const date = cellDate.date();
            const today = this.helperService.getCurrentDateByTimeZone(this.tz);
            let cellClass = today.getDate() === date &&
                today.getMonth() == new Date(cellDate).getMonth() &&
                today.getFullYear() == new Date(cellDate).getFullYear()
                ? 'current__date' : 'no_current_date';
            return (new Date(cellDate).getDay() == 6 || new Date(cellDate).getDay() == 0) ? cellClass + ' weekend-day' : cellClass
        }
        return '';
    };
    ngOnInit(): void {
        this.translateStrings();
        this.dateAdapter.setLocale(this.locale);
        this.datePlaceholder = this.userProfile.dateFormat.toUpperCase();
        this.manualScheduleForm = this.followupScheduleForm.get('discSchedule') as UntypedFormGroup;
        this.discDates = this.manualScheduleForm.get("discreteDates") as UntypedFormArray;
        this.manualScheduleForm.valueChanges.pipe(debounceTime(1000),
            distinctUntilChanged()).subscribe(() => {
                this.manualScheduleForm.get('disSchdChangedFlg').setValue(this.manualScheduleForm.dirty);
            })

        this.discDates.at(0).get('date').setValidators(Validators.required)
    }

    translateStrings(){
        this.labels = this.localizationService.appendStringLabelsToRecord(this.labels, this.stringLabels);
    }

    translatePlaceHolder(): string {
        let key = this.datePlaceholder?.toUpperCase() == 'DD-MM-YYYY'
            ? 'label.gb.ddmmyyyy' :
            this.datePlaceholder?.toUpperCase() == 'MM-DD-YYYY'
                ? 'label.gb.mmddyyyy' : 'label.gb.yyyymmdd';
        return this.localizationService.generateLocalizedStr(key);
    }

    orgValueChange($event, index) {
        this.manualScheduleForm.get('disSchdChangedFlg').setValue(true);
        const changedControl = this.discDates.at(index).get('date');
        if(index == 0 && [null,''].includes($event)){
            changedControl?.setErrors({ required: true });
            return;
        }
        if(typeof $event === 'string' && ![null,''].includes($event) && !this.helperService.dateValidator($event, this.userProfile.dateFormat)) {
            changedControl?.setErrors({ invalidDate: true });
        } else {
            changedControl?.setErrors(null);
        }
        changedControl?.markAsTouched();
    }

    disCreteDates(): UntypedFormArray {
        return this.manualScheduleForm.get("discreteDates") as UntypedFormArray
    }

    calculateFirstInterval(index: number) {
        if (index == 0) {
            if (this.lastTransMissionDate == '' || this.lastTransMissionDate == null) { return '---'; }
            const zeroIndexDate = (this.manualScheduleForm.get('discreteDates') as UntypedFormArray).at(0).get('date').value;
            const ymdLastTranmissionDate = this.helperService.getDateInYMDFormat(this.lastTransMissionDate, this.datePlaceholder)
            if (new Date(ymdLastTranmissionDate) < new Date(zeroIndexDate)) {
                const msInDay = 24 * 60 * 60 * 1000;
                return Math.round(Math.abs(Number(new Date(zeroIndexDate)) - Number(new Date(ymdLastTranmissionDate))) / msInDay) +  ' ' + this.translateService.instant('label.gb.days');
            } else {
                return '---';
            }
        } else {
            const prevIndex = index - 1;
            const prevDate = (this.manualScheduleForm.get('discreteDates') as UntypedFormArray).at(prevIndex).get('date').value;
            const indexDate = (this.manualScheduleForm.get('discreteDates') as UntypedFormArray).at(index).get('date').value;
            if ([null, ''].includes(prevDate) || [null, ''].includes(indexDate)) { return '---'; }
            if (![null, ''].includes(prevDate) && ![null, ''].includes(indexDate)) {
                if (new Date(prevDate) <= new Date(indexDate)) {
                    const msInDay = 24 * 60 * 60 * 1000;
                    return Math.round(Math.abs(Number(indexDate) - Number(prevDate)) / msInDay) + ' ' + this.translateService.instant('label.gb.days');
                } else {
                    return '---';
                }
            }
        }
        return '---';
    }
    calculateInterval(num1, num2) {
        return '..';
    }
    dateDiscreteChanged($event, index) {
        (this.manualScheduleForm.get('discreteDates') as UntypedFormArray).at(index).get('date').setValue($event)
    }
}